<template lang="pug">
div(
    :id="id",
    :class="{ 'cell-is-empty': isEmptyCell }"
)
    span.delete-cell.text-muted(
        v-if="!isEmptyCell && canManageOrderLayout && !isLoading",
        @click="removeCell"
    )
        i.material-icons close

    div(:style="style")
        .order-body(
            :class="{ 'cell-has-value': !isEmptyCell, 'cell-selected': isSelected }",
            @click="setSelectedCell",
        )
            .order-content
                div.cell-empty(v-if="isEmptyCell")
                    .dropdown(v-if="canManageOrderLayout && !isLoading")
                        i.material-icons.text-2x.pointer(
                            data-toggle="dropdown",
                            aria-haspopup="true",
                            aria-expanded="false"
                        ) add

                        .dropdown-menu
                            a.dropdown-item(@click="addProduct") Add Product
                            a.dropdown-item(@click="addVariant") Add Variant
                            a.dropdown-item(v-if="canManagePromotions" @click="addPromotion") Add Promotion
                            a.dropdown-item(@click="addGroup") Add Display Group

                div(v-else)
                    //- product
                    div(v-if="actionType == 'product'")
                        span.clear._600.text-shadow(v-show="cell.action.code !== null") {{ cell.action.code }}
                        span.small.text-shadow {{ cell.action.name }}

                        .upload-enabled(
                            v-if="!uploadStatus"
                            :class="{'upload-disabled': !cell.action.allow_media_override}"
                        )
                            svg-icon(icon="add-photo" :growByHeight="false")

                        .upload-icon(v-if="uploadStatus")
                            i.material-icons {{ uploadStatus }}

                    //- variant
                    div(v-if="actionType == 'variant'")
                        span.clear.text-lg(v-show="cell.action.product.code !== null") {{ cell.action.product.code }}
                        span {{ cell.action.product.name }}
                        span.block(:style="{ color: 'rgb(253, 178, 100)' }") {{ cell.action.name }}

                    div(v-if="actionType == 'group'")
                        button.view-group.btn.btn-sm.btn-icon.white.text-dark(@click="viewGroupPages", :style="{ position: 'absolute', right: '5px', top: '5px' }")
                            i.material-icons keyboard_arrow_right
                        span.clear.text-md.text-shadow {{ cell.action.name }}

                    div(v-if="actionType == 'promotion'")
                        span.clear.text-shadow {{ cell.action.name }}
                        small.text-shadow(:style="{ color: 'rgb(253, 178, 100)' }") {{ promotionType(cell.action) }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { OrderLayoutEvent } from './order-layout-bus';
import LoadingMixin from './mixins/loading';
import PermissionMixin from './order-layout-permission';
import PromotionPermissionMixin from '@js/franchise_component/promotion/mixin/promotion-permissions';
import PromotionTypes from '@js/store/promotion-types';
import SvgIcon from '@js/components/svg-icon';

export default {
    components: {
        SvgIcon,
    },

    mixins: [PermissionMixin, PromotionPermissionMixin, LoadingMixin],

    props: {
        x: {
            type: Number,
            required: true,
        },
        y: {
            type: Number,
            required: true,
        },
    },

    computed: {
        ...mapGetters('orderLayout', [
            'orderLayout',
            'getPageCellByGrid',
            'activePage',
            'hasSelectedGrid',
            'hasActivePage',
            'selectedGrid',
            'getCellPendingMedia',
        ]),

        id() {
            return `${this.x},${this.y}`;
        },

        hasColor() {
            return !this.isEmptyCell && !_.isNull(this.cell.color);
        },

        style() {
            const style = { overflow: this.isEmptyCell ? 'visible' : '' };
            style['height'] = '100%';

            if (this.isEmptyCell) return style;
            if (this.actionType == 'group') style['position'] = 'relative';

            style['color'] = 'rgb(239, 235, 235)';
            if (!_.isNull(this.productImage)) {
                style[
                    'background-image'
                ] = `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('${
                    this.productImage
                }')`;
                style['background-size'] = 'cover';
                style['background-repeat'] = 'no-repeat';
            } else {
                if (this.hasColor) {
                    style.background = this.cell.color;
                } else {
                    // default color
                    style.background = 'rgb(123, 123, 123)';
                }
            }

            return style;
        },

        productImage() {
            if (!this.isEmptyCell && this.actionType == 'product') {
                if (this.media) {
                    return this.media.data.local_url;
                }

                const action = this.cell.action;
                const omu = action.override_media_urls;
                const cmu = action.media_urls;
                const canOverride = action.allow_media_override;

                if (
                    omu &&
                    omu.length &&
                    !this.cell.remove_media_id &&
                    canOverride
                ) {
                    return omu[omu.length - 1].s512;
                }

                if (cmu && cmu.length) {
                    return cmu[0].s512;
                }
            }

            if (
                !this.isEmptyCell &&
                this.actionType == 'variant' &&
                action.product.media_urls.length
            ) {
                return action.product.media_urls[0].s512;
            }

            return null;
        },

        uploadStatus() {
            if (this.media) {
                if (this.media.success) {
                    return 'cloud_done';
                }

                if (this.media.active) {
                    return 'cloud_upload';
                }

                if (this.media.error) {
                    return 'cloud_off';
                }

                return 'cloud_queue';
            }

            return null;
        },

        media() {
            const m = this.getCellPendingMedia(this.cell);

            if (m.length) {
                return m[0];
            }

            return null;
        },

        cell() {
            return this.getPageCellByGrid(this.activePage.id, this.x, this.y);
        },

        grid() {
            return { x: this.x, y: this.y };
        },

        actionType() {
            return this.cell.action_type;
        },

        isEmptyCell() {
            return (
                _.isNull(this.cell) ||
                _.isNull(this.cell.action_type) ||
                _.isNull(this.cell.action)
            );
        },

        isSelected() {
            if (
                !this.hasActivePage ||
                this.isEmptyCell ||
                !this.hasSelectedGrid
            )
                return false;

            return (
                this.activePage.id == this.selectedGrid.page_id &&
                this.x == this.selectedGrid.x &&
                this.y == this.selectedGrid.y
            );
        },
    },

    methods: {
        ...mapActions('orderLayout', [
            'removePageCell',
            'viewGroup',
            'setSelectedGrid',
        ]),

        addProduct() {
            OrderLayoutEvent.$emit('add-product-cell', this.grid);
        },

        addVariant() {
            OrderLayoutEvent.$emit('add-variant-cell', this.grid);
        },

        addGroup() {
            OrderLayoutEvent.$emit('add-group-cell', this.grid);
        },

        addPromotion() {
            OrderLayoutEvent.$emit('add-promotion-cell', this.grid);
        },

        viewGroupPages() {
            this.viewGroup(this.cell.action.id);
            this.setSelectedGrid();
        },

        async setSelectedCell(e) {
            const elem = $(e.target);

            if (
                this.isEmptyCell ||
                elem.hasClass('view-group') ||
                elem.parent().hasClass('view-group') ||
                this.isLoading
            )
                return;

            const grid = await this.setSelectedGrid({
                page_id: this.activePage.id,
                x: this.x,
                y: this.y,
            });
        },

        promotionType(promotion) {
            let type = PromotionTypes.find(t => t.value === promotion.type);
            return type ? type.name : 'Unknown';
        },

        removeCell() {
            this.removePageCell({
                page_id: this.activePage.id,
                x: this.x,
                y: this.y,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.delete-cell {
    position: absolute;
    margin: -8px;
    font-size: 16px;
    background: #fff;
    opacity: 0.9;
    border-radius: 20px;
    -webkit-border-radius: 20px;
    -moz-border-radius: 20px;
    padding: 2px;
    cursor: pointer;
    z-index: 1;
}

.order-body {
    position: relative;
}

.upload-icon,
.upload-enabled {
    bottom: 4px;
    position: absolute;
    right: 4px;
}

.upload-enabled {
    font-size: 18px;
    fill: #fff;
}

.upload-disabled {
    opacity: 0.5;
    fill: #ccc;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/order-layout/order-layout-box.vue